// FE-SCRM-048 — Step 1: drop-zone CSV upload with a Download template
// shortcut. We expose a hidden <input type="file"> so the visible
// drop-zone is both clickable and drop-target; the wizard owns the
// mutation so we just hand the selected file up via a callback.

import { useRef, useState, type DragEvent, type ChangeEvent } from "react";

import { Button } from "@/design-system";

import { downloadTemplate } from "./templates";
import { IMPORT_TYPE_LABEL, type ImportType } from "./types";


export interface UploadStepProps {
  import_type: ImportType;
  on_file: (file: File) => void;
  is_uploading: boolean;
  upload_error?: string | null;
}


export function UploadStep({
  import_type, on_file, is_uploading, upload_error,
}: UploadStepProps) {
  const input_ref = useRef<HTMLInputElement | null>(null);
  const [is_dragging, set_dragging] = useState(false);

  function pickFiles(file_list: FileList | null) {
    const file = file_list?.[0];
    if (file) on_file(file);
  }

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    pickFiles(e.target.files);
    // Reset so re-uploading the same filename still fires onChange.
    e.target.value = "";
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    set_dragging(false);
    pickFiles(e.dataTransfer.files);
  }

  return (
    <div
      data-testid="import-step-upload"
      className="flex flex-col gap-4"
    >
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-text-primary">
            Upload {IMPORT_TYPE_LABEL[import_type]} CSV
          </h2>
          <p className="text-xs text-text-secondary">
            CSV only, max 1000 rows. Need to import more? Split into
            multiple files.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => downloadTemplate(import_type)}
        >
          Download template
        </Button>
      </header>

      <div
        data-testid="import-upload-dropzone"
        onClick={() => input_ref.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          set_dragging(true);
        }}
        onDragLeave={() => set_dragging(false)}
        onDrop={onDrop}
        className={[
          "flex flex-col items-center justify-center gap-2",
          "rounded-lg border-2 border-dashed px-6 py-12",
          "cursor-pointer transition-colors",
          is_dragging
            ? "border-teal bg-teal-tint"
            : "border-border-subtle bg-recessed hover:border-border-hover",
        ].join(" ")}
      >
        <span className="text-sm font-medium text-text-primary">
          Drop your CSV here, or click to choose a file
        </span>
        <span className="text-xs text-text-tertiary">
          {is_uploading
            ? "Uploading and validating…"
            : "We'll validate before anything is created."}
        </span>
        <input
          ref={input_ref}
          data-testid="import-upload-input"
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={onChange}
          disabled={is_uploading}
        />
      </div>

      {upload_error && (
        <p
          data-testid="import-upload-error"
          className="text-xs text-ember-ink"
        >
          {upload_error}
        </p>
      )}
    </div>
  );
}
